import { Text } from "@design-system/Typography/Text";
import { cn } from "@design-system/Utilities";
import { Slot } from "@radix-ui/react-slot";
import {
    type FC,
    type HTMLAttributes,
    type PropsWithChildren,
    type Ref,
} from "react";

type BreadcrumbComponentType = FC<
    HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> }
> & {
    List: typeof BreadcrumbList;
    Item: typeof BreadcrumbItem;
    Link: typeof BreadcrumbLink;
    Separator: typeof BreadcrumbSeparator;
};

const BreadcrumbList: FC<
    HTMLAttributes<HTMLOListElement> & { ref?: Ref<HTMLOListElement> }
> = ({ className, ref, ...props }) => {
    return (
        <ol
            ref={ref}
            className={cn(
                "flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground",
                className,
            )}
            {...props}
        />
    );
};

const BreadcrumbItem: FC<
    HTMLAttributes<HTMLLIElement> & { ref?: Ref<HTMLLIElement> }
> = ({ className, ref, ...props }) => {
    return (
        <li
            ref={ref}
            className={cn(
                "inline-flex items-center gap-1.5 text-primary",
                className,
            )}
            {...props}
        />
    );
};

type BreadcrumbLinkProps = HTMLAttributes<HTMLAnchorElement> & {
    ref?: Ref<HTMLAnchorElement>;
    asChild?: boolean;
    href?: string;
};

const BreadcrumbLink: FC<BreadcrumbLinkProps> = ({
    asChild,
    className,
    ref,
    ...props
}) => {
    const Comp = asChild ? Slot : "a";

    return (
        <Comp
            ref={ref}
            className={cn(
                "transition-colors text-sm font-bold text-primary",
                "hover:no-underline hover:text-beavrGreen",
                className,
            )}
            {...props}
        />
    );
};

const BreadcrumbSeparator: FC<
    PropsWithChildren<HTMLAttributes<HTMLLIElement>> & {
        ref?: Ref<HTMLLIElement>;
    }
> = ({ children, ref, ...props }) => {
    return (
        <li role="presentation" aria-hidden="true" {...props} ref={ref}>
            {children ?? (
                <Text variant="body-sm" color="secondary">
                    /
                </Text>
            )}
        </li>
    );
};

export const Breadcrumb: BreadcrumbComponentType = ({ ref, ...props }) => {
    return <nav ref={ref} aria-label="breadcrumb" {...props} />;
};

Breadcrumb.List = BreadcrumbList;
Breadcrumb.Item = BreadcrumbItem;
Breadcrumb.Link = BreadcrumbLink;
Breadcrumb.Separator = BreadcrumbSeparator;
